import React from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  useMediaQuery,
  Paper,
} from "@mui/material";
import type { AutocompleteRenderInputParams, SxProps, Theme } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ArrowDown2, CloseCircle } from "iconsax-react";
import { useCompany } from "../../../hooks/useCompany";
import { type CompanyDto } from "../../../services";
import { useTranslation } from "react-i18next";
import { RADIUS } from "../../../theme/mixins";

interface CompanySelectProps {
  label?: string;
  onChange?: (companyId: string, companyName: string) => void;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  showAllOption?: boolean;
  sx?: SxProps<Theme>;
  variant?: "light" | "dark"; // light = light bg with dark text (default), dark = dark bg with light text
}

// styled components moved outside the render to prevent remount/focus loss
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.common.white,
    borderRadius: `${RADIUS.input}px`,
    fontSize: "14px",
    fontWeight: 600,
    minHeight: "44px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.border.strong,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.border.grey,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  "& .MuiAutocomplete-option": {
    fontSize: "14px",
    color: theme.palette.text.primary,
    fontWeight: 400,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&[aria-selected='true']": {
      backgroundColor: `${theme.palette.action.selected} !important`,
      color: theme.palette.text.primary,
      fontWeight: 600,
      "&:hover": {
        backgroundColor: `${theme.palette.action.selected} !important`,
      },
    },
    "&.Mui-selected": {
      backgroundColor: `${theme.palette.action.selected} !important`,
      color: theme.palette.text.primary,
      fontWeight: 600,
      "&:hover": {
        backgroundColor: `${theme.palette.action.selected} !important`,
      },
    },
  },
}));

export const CompanySelect: React.FC<CompanySelectProps> = ({
  label = "Company",
  onChange,
  value: companyId,
  required = false,
  disabled = false,
  showAllOption = false,
  sx,
  variant = "light", // Default to light variant (light bg with dark text)
}) => {
  const { availableCompanies, isLoading, error } = useCompany();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));

  // Determine colors based on variant (matching SingleSelect pattern)
  const getColors = () => {
    if (variant === "dark") {
      // Dark variant: light text for dark backgrounds
      const defaultColor = isSmallScreen
        ? theme.palette.text.disabled
        : theme.palette.common.white;
      return {
        textColor: defaultColor,
        iconColor: defaultColor,
        labelColor: theme.palette.primary.main,
        labelFocusedColor: theme.palette.primary.main,
      };
    } else {
      // Light variant: dark text for light backgrounds (default)
      return {
        textColor: theme.palette.text.primary,
        iconColor: isSmallScreen
          ? theme.palette.text.disabled
          : theme.palette.grey[500],
        labelColor: theme.palette.primary.main,
        labelFocusedColor: theme.palette.primary.main,
      };
    }
  };

  const { textColor, iconColor } = getColors();

  // Type for autocomplete options (union of CompanyDto and "All" option)
  type CompanyOption = CompanyDto | { id: string; name: string; active: boolean };

  const { t } = useTranslation();

  // Create options list with optional "All" option
  const options: CompanyOption[] = React.useMemo(() => {
    const baseOptions: CompanyDto[] = availableCompanies || [];
    if (showAllOption) {
      return [{ id: "all", name: t("companies.allCompanies"), active: true }, ...baseOptions];
    }
    return baseOptions;
  }, [availableCompanies, showAllOption, t]);

  // Find the currently selected company
  const selectedCompany: CompanyOption | null = options.find(
    (company) => company.id === companyId
  ) || null;

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: CompanyOption | null
  ) => {
    if (!onChange) return;

    if (newValue) {
      onChange(newValue.id, newValue.name);
      return;
    }

    // Clearing the field defaults back to "All Companies" when that option exists,
    // since selecting "all" still satisfies a required selection.
    if (showAllOption) {
      const allOption = options.find((option) => option.id === "all");
      onChange(allOption?.id ?? "", allOption?.name ?? "");
      return;
    }

    if (required) return;

    onChange("", "");
  };

  if (error) {
    return (
      <div>
        <StyledTextField
          fullWidth
          variant="outlined"
          disabled
          label={label}
          value="Error loading companies"
          sx={{
            "& .MuiOutlinedInput-root": {
              color: textColor,
            },
            ...sx,
          }}
        />
        <Alert severity="error" sx={{ mt: 1, fontSize: "12px" }}>
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: CompanyOption) => option.name || ""}
      value={selectedCompany}
      onChange={handleChange}
      loading={isLoading}
      disabled={disabled}
      isOptionEqualToValue={(option: CompanyOption, value: CompanyOption) =>
        option.id === value.id
      }
      slots={{
        paper: StyledPaper,
      }}
      popupIcon={<ArrowDown2 size={16} color={iconColor} style={{ marginRight: 6 }} />}
      clearIcon={<CloseCircle size={14} color={iconColor} variant="Bold" />}
      disableClearable={required && !showAllOption}
      sx={{
        "& .MuiAutocomplete-popupIndicator": {
          border: "none",
          outline: "none",
          boxShadow: "none",
          padding: 0,
          marginRight: 0,
          width: 24,
          transform: "none !important",
          "&:hover": {
            border: "none",
            backgroundColor: "transparent",
          },
          "&.Mui-expanded": {
            transform: "none !important",
          },
        },
        "& .MuiAutocomplete-clearIndicator": {
          border: "none",
          outline: "none",
          boxShadow: "none",
          padding: 0,
          width: 18,
          height: 18,
          marginRight: 2,
          "&:hover": {
            border: "none",
            backgroundColor: "transparent",
          },
        },
        ...sx,
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <StyledTextField
          {...params}
          placeholder={label || "Bedrijf"}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: textColor,
              "& .MuiAutocomplete-input": { color: textColor },
              "& .MuiAutocomplete-input::placeholder": {
                color: theme.palette.text.disabled,
                opacity: 1,
              },
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress size={20} sx={{ marginRight: 2 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default CompanySelect;